import { useCallback, useEffect } from 'react';

import * as Ably from 'ably';
import { useChannel } from 'ably/react';

import { CallerCommunicationApi } from '@/api/api.caller-communication';
import { useCallerCommunicationStore, useUserStore } from '@/store';

interface UnreadCountsResponse {
  data: Record<string, number>;
}

interface NewLocationCountsResponse {
  data: Record<string, number>;
}

interface MessageAddedEvent {
  callerLink: string;
  sender: 'CALLER' | 'DISPATCHER' | 'SYSTEM';
}

interface LocationAddedEvent {
  _id: string;
  callerLink: string;
  isNew?: boolean;
}

export const useCFSCallerBadges = (cfsId: string | undefined): void => {
  const { account } = useUserStore();
  const {
    setUnreadCounts,
    incrementUnreadFor,
    setNewLocationCounts,
    incrementNewLocationFor,
    reset,
  } = useCallerCommunicationStore();

  const channelName =
    account?._id && cfsId
      ? `account:${account._id}:cfs:${cfsId}:caller-communication`
      : '';

  useChannel(
    channelName,
    useCallback(
      (message: Ably.Types.Message) => {
        if (!message?.name || !message.data) return;

        if (message.name === 'message-added') {
          const event = message.data as MessageAddedEvent;
          if (event.sender !== 'CALLER') return;
          const viewing =
            useCallerCommunicationStore.getState().currentlyViewingCallerLinkId;
          if (viewing === event.callerLink) return;
          incrementUnreadFor(event.callerLink);
          return;
        }

        if (message.name === 'location-added') {
          const event = message.data as LocationAddedEvent;
          if (!event?.isNew) return;
          incrementNewLocationFor(event.callerLink);
        }
      },
      [cfsId, channelName, incrementUnreadFor, incrementNewLocationFor],
    ),
  );

  useEffect(() => {
    if (!cfsId) {
      reset();
      return;
    }

    let cancelled = false;

    const fetchInitial = async () => {
      try {
        const [unreadResp, newLocResp] = await Promise.all([
          CallerCommunicationApi.getUnreadCounts(cfsId),
          CallerCommunicationApi.getNewLocationCounts(cfsId),
        ]);
        if (cancelled) return;

        const unreadPayload = (
          unreadResp.data as UnreadCountsResponse | undefined
        )?.data;
        const nextUnread = new Map<string, number>();
        if (unreadPayload) {
          for (const [callerLinkId, count] of Object.entries(unreadPayload)) {
            if (typeof count === 'number' && count > 0) {
              nextUnread.set(callerLinkId, count);
            }
          }
        }
        setUnreadCounts(nextUnread);

        const newLocPayload = (
          newLocResp.data as NewLocationCountsResponse | undefined
        )?.data;
        const nextNewLoc = new Map<string, number>();
        if (newLocPayload) {
          for (const [callerLinkId, count] of Object.entries(newLocPayload)) {
            if (typeof count === 'number' && count > 0) {
              nextNewLoc.set(callerLinkId, count);
            }
          }
        }
        setNewLocationCounts(nextNewLoc);
      } catch {
        if (cancelled) return;
        setUnreadCounts(new Map());
        setNewLocationCounts(new Map());
      }
    };

    fetchInitial();

    return () => {
      cancelled = true;
      reset();
    };
  }, [cfsId, setUnreadCounts, setNewLocationCounts, reset]);
};
